import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../i18n';
import { toast } from 'sonner';

const ForgotPasswordPage = () => {
  const { requestPasswordReset } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setError('');
    setLoading(true);
    try {
      await requestPasswordReset(email.trim().toLowerCase());
      setSent(true);
      toast.success(language === 'pt' ? 'Pedido recebido!' : 'Request received!');
    } catch (err) {
      console.error('Password reset error:', err);
      const detail = err?.response?.data?.detail;
      if (typeof detail === 'string' && (detail.includes('Google') || detail.includes('convidado'))) {
        setError(language === 'pt'
          ? 'Esta conta não utiliza uma palavra-passe local. Se entras com Google, escolhe «Continuar com Google».'
          : 'This account does not use a local password. Please sign in with Google if applicable.');
      } else {
        setError(typeof detail === 'string'
          ? detail
          : (language === 'pt' ? 'Não foi possível processar o pedido. Tenta novamente.' : 'Unable to process your request. Please try again.'));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="min-h-[100svh] bg-[#F9F8F6] pt-24 pb-16 flex items-center justify-center px-4"
      style={{ paddingBottom: 'max(64px, env(safe-area-inset-bottom, 0px))' }}
      data-testid="forgot-password-page">
      <div className="w-full max-w-md">
        {sent ? (
          <div className="bg-white rounded-3xl p-5 sm:p-8 shadow-lg border border-[#E6E2DA] text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h1 className="font-heading text-2xl font-medium text-[#1A1815] mb-2">
              {language === 'pt' ? 'Pedido recebido!' : 'Request received!'}
            </h1>
            <p className="text-[#635E54] mb-6 text-sm leading-relaxed">
              {language === 'pt'
                ? `Se existir uma conta com palavra-passe associada a ${email.trim().toLowerCase()}, receberás as instruções por email. Verifica também a pasta de spam.`
                : `If there is a password-based account associated with ${email.trim().toLowerCase()}, you will receive instructions by email. Please also check your spam folder.`}
            </p>
            <Button onClick={() => navigate('/login')} className="min-h-12 w-full bg-[#1A1815] text-white hover:bg-[#2C2924] rounded-full">
              {language === 'pt' ? 'Voltar ao início de sessão' : 'Back to login'}
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <h1 className="font-heading text-3xl font-medium text-[#1A1815]">
                {language === 'pt' ? 'Esqueceste-te da palavra-passe?' : 'Forgot your password?'}
              </h1>
              <p className="text-[#635E54] mt-2 text-sm">
                {language === 'pt'
                  ? 'Insere o teu email para receberes as instruções de recuperação.'
                  : 'Enter your email to receive password reset instructions.'}
              </p>
            </div>
            <div className="bg-white rounded-3xl p-5 sm:p-8 shadow-lg border border-[#E6E2DA]">
              {error && <div role="alert" className="mb-4 p-4 bg-red-50 border border-red-200 rounded-2xl"><p className="text-sm text-red-700">{error}</p></div>}
              <form onSubmit={handleSubmit} className="space-y-4">
                <label htmlFor="forgot-email" className="sr-only">Email</label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A8A29A]" />
                  <Input id="forgot-email" type="email" name="email" autoComplete="email" placeholder="Email"
                    value={email} onChange={e => setEmail(e.target.value)}
                    className="h-12 pl-12 rounded-full border-[#E6E2DA] focus:border-[#BFA05F] text-base"
                    required data-testid="forgot-email" />
                </div>
                <Button type="submit" disabled={loading}
                  className="min-h-12 w-full bg-[#BFA05F] text-white hover:bg-[#9E8246] rounded-full"
                  data-testid="forgot-submit">
                  {loading ? (language === 'pt' ? 'A enviar…' : 'Sending…') : (language === 'pt' ? 'Enviar email de recuperação' : 'Send reset email')}
                </Button>
              </form>
              <div className="mt-6 text-center">
                <Link to="/login" className="min-h-11 text-sm text-[#635E54] hover:text-[#BFA05F] inline-flex items-center">
                  <ArrowLeft className="w-4 h-4 mr-1" />{language === 'pt' ? 'Voltar ao início de sessão' : 'Back to login'}
                </Link>
              </div>
            </div>
          </>
        )}
      </div>
    </main>
  );
};

export default ForgotPasswordPage;
